import React, { useState, useEffect } from 'react';
import { ShieldCheck, Globe, ChevronDown, CheckCircle2, Menu, X } from 'lucide-react';
import { getCurrentLanguage, setLanguage, getLanguageName } from '../services/i18n';
import { useLanguageUpdate } from '../src/hooks/useLanguageChange';

interface CommonHeaderProps {
  onLogin?: () => void;
  onGetStarted?: () => void;
  onNavigateHome?: () => void;
  currentUser?: any;
  onMenuClick?: () => void;
  showMenuButton?: boolean;
}

export const CommonHeader: React.FC<CommonHeaderProps> = ({ 
  onLogin, 
  onGetStarted, 
  onNavigateHome,
  currentUser,
  onMenuClick,
  showMenuButton = false
}) => {
  useLanguageUpdate();
  
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [currentLang, setCurrentLang] = useState<string>(getCurrentLanguage());
  
  const languages = [
    { code: 'en' as const, name: 'English', flag: '🇺🇸' },
    { code: 'fr' as const, name: 'Français', flag: '🇫🇷' },
    { code: 'ar' as const, name: 'العربية', flag: '🇸🇦' },
    { code: 'darija' as const, name: 'Darija', flag: '🇲🇦' },
    { code: 'de' as const, name: 'Deutsch', flag: '🇩🇪' },
    { code: 'es' as const, name: 'Español', flag: '🇪🇸' },
  ];

  useEffect(() => {
    const savedLang = localStorage.getItem('orca_ui_language_code') || 'en';
    setCurrentLang(savedLang);
    setLanguage(savedLang as any);
    
    const handleStorageChange = () => {
      const newLang = localStorage.getItem('orca_ui_language_code') || 'en';
      if (newLang !== currentLang) {
        setCurrentLang(newLang);
        setLanguage(newLang as any);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    const interval = setInterval(handleStorageChange, 500);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [currentLang]);

  const handleLanguageChange = async (lang: 'en' | 'fr' | 'ar' | 'darija' | 'de' | 'es') => {
    await setLanguage(lang);
    setCurrentLang(lang);
    setShowLangMenu(false);
    localStorage.setItem('orca_ui_language_code', lang);
    if (lang === 'ar' || lang === 'darija') {
      document.documentElement.setAttribute('dir', 'rtl');
    } else {
      document.documentElement.setAttribute('dir', 'ltr');
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showLangMenu && !target.closest('.language-menu-container')) {
        setShowLangMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showLangMenu]);

  useEffect(() => {
    if (currentLang === 'ar' || currentLang === 'darija') {
      document.documentElement.setAttribute('dir', 'rtl');
    } else {
      document.documentElement.setAttribute('dir', 'ltr');
    }
  }, [currentLang]);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0a0e27]/80 backdrop-blur-xl border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 md:h-20 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {showMenuButton && onMenuClick && (
            <button
              onClick={onMenuClick}
              className="md:hidden text-slate-300 hover:text-white p-2"
            >
              <Menu size={24} />
            </button>
          )}
          <button 
            onClick={onNavigateHome} 
            className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
            title="Go to Home"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-blue-500 rounded-full blur-lg opacity-50 animate-pulse"></div>
              <div className="relative bg-gradient-to-br from-blue-500 to-purple-600 p-2 rounded-xl">
                <ShieldCheck className="w-6 h-6 text-white" />
              </div>
            </div>
            <span className="font-extrabold text-xl md:text-2xl tracking-tight bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
              Orca<span className="text-blue-400">Tools</span>
            </span>
          </button>
        </div>
        <div className="flex items-center gap-2 md:gap-4">
          {/* Language Switcher */}
          <div className="relative language-menu-container">
            <button
              onClick={() => setShowLangMenu(!showLangMenu)}
              className="flex items-center gap-1 md:gap-2 px-2 md:px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 transition-all text-slate-300 hover:text-white"
            >
              <Globe size={16} className="text-indigo-400 md:w-[18px] md:h-[18px]" />
              <span className="text-xs md:text-sm font-medium">
                {languages.find(l => l.code === currentLang)?.flag}
              </span>
              <span className="hidden md:inline text-xs md:text-sm font-medium">
                {languages.find(l => l.code === currentLang)?.name || 'English'}
              </span>
              <ChevronDown size={14} className={`transition-transform ${showLangMenu ? 'rotate-180' : ''} hidden md:block`} />
            </button>
            {showLangMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-slate-800 border border-slate-700 rounded-xl shadow-2xl overflow-hidden z-50 animate-slideUp">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => handleLanguageChange(lang.code)}
                    className={`w-full px-4 py-3 text-left hover:bg-slate-700 transition-colors flex items-center gap-3 ${
                      currentLang === lang.code ? 'bg-indigo-500/20 text-indigo-400' : 'text-slate-300'
                    }`}
                  >
                    <span className="text-lg">{lang.flag}</span>
                    <span className="font-medium">{lang.name}</span>
                    {currentLang === lang.code && <CheckCircle2 size={16} className="ml-auto" />}
                  </button>
                ))}
              </div>
            )}
          </div>
          
          {/* Auth Buttons - Show Login/GetStarted if not logged in */}
          {!currentUser && (
            <>
              {onLogin && (
                <button 
                  onClick={onLogin}
                  className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white transition-colors hidden md:block"
                >
                  Login
                </button>
              )}
              {onGetStarted && (
                <button 
                  onClick={onGetStarted}
                  className="px-4 md:px-6 py-2 md:py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white rounded-lg font-bold text-sm md:text-base transition-all shadow-lg shadow-indigo-500/25 hover:shadow-indigo-500/40"
                >
                  Get Started
                </button>
              )}
            </>
          )}

          {/* Dashboard button for logged-in users on non-dashboard pages */}
          {currentUser && !showMenuButton && onNavigateHome && (
            <button 
              onClick={onNavigateHome}
              className="px-4 md:px-6 py-2 md:py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white rounded-lg font-bold text-sm md:text-base transition-all shadow-lg shadow-indigo-500/25 hover:shadow-indigo-500/40"
            >
              Dashboard
            </button>
          )}
        </div>
      </div>
    </nav>
  );
};

